import { Recommendation } from "@/types/dataSchema";
import { findFirstQuestion } from "@/lib/journey-utils";

// Function to calculate estimated time for recommendations
export const getEstimatedTime = (
  recommendation: Recommendation,
): { duration: string; text: string } => {
  // Priority: Use durationInMinutes if present and valid
  if (
    recommendation.durationInMinutes &&
    recommendation.durationInMinutes > 0
  ) {
    const minutes = recommendation.durationInMinutes;

    // Adapt message based on recommendation type and duration
    const getContextualMessage = (minutes: number, type: string) => {
      if (type === "contact") {
        return `Takes typically ${minutes} minute${minutes > 1 ? "s" : ""}`;
      }
      if (type === "course") {
        return `Takes typically ${minutes} minute${minutes > 1 ? "s" : ""}`;
      }
      if (type === "documentation") {
        return `Takes typically ${minutes} minute${minutes > 1 ? "s" : ""} to read`;
      }
      if (type === "notify") {
        return `Takes typically ${minutes} minute${minutes > 1 ? "s" : ""}`;
      }

      // Default setup messages
      return `Takes typically ${minutes} minute${minutes > 1 ? "s" : ""} to setup`;
    };

    if (minutes <= 60) {
      return {
        duration: `${minutes} min`,
        text: getContextualMessage(minutes, recommendation.type),
      };
    } else {
      const hours = Math.ceil(minutes / 60);
      const contextualText =
        recommendation.type === "course"
          ? `Takes typically ${hours} hour${hours > 1 ? "s" : ""}`
          : `Takes typically ${hours} hour${hours > 1 ? "s" : ""}`;

      return {
        duration: `${hours}h`,
        text: contextualText,
      };
    }
  }

  // Fallback: Use default values based on recommendation type
  switch (recommendation.type) {
    case "documentation":
      return { duration: "10 min", text: "Takes typically 10 minutes to read" };
    case "contact":
      return { duration: "2 min", text: "Takes typically 2 minutes" };
    case "course":
      return { duration: "30 min", text: "Takes typically 30 minutes" };
    case "notify":
      return { duration: "1 min", text: "Takes typically 1 minute" };
    default:
      return {
        duration: "15 min",
        text: "Takes typically 15 minutes to setup",
      };
  }
};

// Function to get current valid journey path
export const getCurrentValidPath = (
  journeyData: any,
  currentQuestionId: string | null,
  answers: Record<string, any>,
  getNextQuestionId: any,
): string[] => {
  const questions = journeyData?.questions || [];

  if (!questions.length) return [];

  const firstQuestion = findFirstQuestion(journeyData);

  if (!firstQuestion) return [];

  const path: string[] = [];
  let currentQuestion: string | null = firstQuestion.id;
  const visited = new Set<string>();

  while (currentQuestion && !visited.has(currentQuestion)) {
    visited.add(currentQuestion);
    path.push(currentQuestion);

    // If this is the current question, stop here
    if (currentQuestion === currentQuestionId) {
      break;
    }

    // Check if this question has an answer
    const answer: any = answers[currentQuestion];

    if (!answer) {
      // No answer yet, this is where the path ends
      break;
    }

    // Find the next question based on current answers
    const nextId: string | null = getNextQuestionId({
      questions,
      questionHistory: path,
      currentQuestionId: currentQuestion,
      skipped: answer === "(skipped)",
      selectedOptions: Array.isArray(answer) ? answer : [answer],
      answers,
    });

    currentQuestion = nextId;
  }

  return path;
};

// Function to generate recommendation reasons for admin view
export const getRecommendationReasons = (
  recommendationId: string,
  recommendedRecommendationsKeys: any[],
  journeyData: any,
): {
  strong: { questionName: string; optionLabel: string }[];
  optional: { questionName: string; optionLabel: string }[];
} => {
  const recommendationData = recommendedRecommendationsKeys.find(
    (s) => s.id === recommendationId,
  );

  if (!recommendationData) {
    return { strong: [], optional: [] };
  }

  const questions = journeyData?.questions || [];

  const formatReasons = (reasons: { question: string; answer: string }[]) => {
    return reasons.map(({ question, answer }) => {
      const questionData = questions.find((q: any) => q.id === question);
      const questionName = questionData?.question || question;

      // Find the option to get its label
      const option = questionData?.options?.find(
        (opt: any) => opt.value === answer,
      );
      const optionLabel = option?.label || answer;

      return {
        questionName,
        optionLabel,
      };
    });
  };

  return {
    strong: formatReasons(recommendationData.strong_recommendations),
    optional: formatReasons(recommendationData.optional_recommendations),
  };
};
